import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Inline } from "../Inline";

const SCENARIOS = [
  { v: "flow", label: "data flows continuously, ingest only" },
  { v: "kafka", label: "the stem literally says \"Kafka\"" },
  { v: "compute", label: "compute or aggregate WHILE it streams" },
  { v: "toS3", label: "deliver a stream into S3" },
  { v: "parked", label: "data is already stored in S3, on a schedule" },
];

const ANSWER = {
  flow: "**Kinesis** — the conveyor belt for continuous ingestion. (Use **MSK** only when the stem says Kafka.)",
  kafka: "**Amazon MSK** — managed Kafka. The word \"Kafka\" is the only trigger that beats plain Kinesis.",
  compute: "**Flink** — the machine that works ON the belt. It's the only option that transforms data mid-stream; Athena, Glue, and QuickSight all need the data to land first.",
  toS3: "**Firehose** — the truck that takes items off the belt and drops them in S3 (and can convert to Parquet). Its arrow only points stream → S3.",
  parked: "**Glue** — parked data on a schedule is batch ETL, not streaming. Every Firehose option is dead the moment data is \"already in S3.\"",
};

export function IngestionPicker() {
  const [v, setV] = useState("flow");
  const answer = useMemo(() => ANSWER[v], [v]);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="my-12 border-2 border-[#2F6F4F] bg-white/70 backdrop-blur-sm p-7 md:p-9"
      data-testid="widget-ingestion-picker"
    >
      <div className="font-mono-ui text-[10px] font-bold uppercase tracking-[0.22em] text-[#2F6F4F] mb-2">
        Try it · Batch or streaming? Pick the tool
      </div>
      <p className="text-[13px] text-[#1C2430]/55 mb-5">Choose what the scenario says and watch the right service surface.</p>
      <label className="flex flex-col gap-1.5 mb-4 max-w-md">
        <span className="font-mono-ui text-[10px] uppercase tracking-[0.12em] text-[#1C2430]/55">The scenario says…</span>
        <select
          value={v}
          onChange={(e) => setV(e.target.value)}
          data-testid="ingestion-select"
          className="font-mono-ui text-[12.5px] bg-white border border-[var(--border-heavy)] px-3 py-2 focus:outline-none focus:border-[#1C2430] cursor-pointer"
        >
          {SCENARIOS.map((s) => (
            <option key={s.v} value={s.v}>{s.label}</option>
          ))}
        </select>
      </label>
      <div className="bg-black/[0.03] px-4 py-3 text-[13.5px] leading-relaxed text-[#1C2430]/85 min-h-[48px]" data-testid="ingestion-output">
        <Inline text={answer} />
      </div>
    </motion.div>
  );
}
